import {
  FunctionComponent,
  useRef,
  useCallback,
  useEffect,
  useState,
} from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useParams, useLocation } from 'react-router-dom'
import useStyles from '@/modules/Auth/pages/Login/styles'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import services from '../../auth-services'
import { IUser } from '@/modules/Auth/auth-types'
import { getThemeByUniqueCode } from '@/theme'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import { initializeBrowserNotification } from '@/components/BrowserNotification'
import { setCache, getCache } from '@/utils/cache'
import { RenderIf } from '@/components/RenderIf'
import { handleLoginError } from './utils'
import {
  Text,
  Button,
  Input,
  ConexaLogo,
  InputPassword,
} from '@conexasaude/conexa-hero'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { MainLayout } from '@/modules/Activation/components/MainLayout'
import { useNavigation } from '@/hooks/useNavigation'
import { type LoginParams, type LoginLocation, LoginFormData } from './types'
import { SEND_TO } from '@/modules/Signup/constants'
import { AnalyticEvent } from '@/modules/Signup/types/en-us'
import { usePatientRegistrationStore } from '@/modules/Signup/store/PatientRegistration.store'
import { loginSchema } from './utils/schemas'
import { isCPFOrEmail } from '@/utils/validations'

const Login: FunctionComponent = () => {
  const params = useParams<LoginParams>()
  const location = useLocation<LoginLocation>()
  const navigate = useNavigation()
  const [loading, setLoading] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })
  const [imageError, setImageError] = useState(false)
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const { theme, setThemeColors } = useThemeGeneratorContext()
  const { signIn } = useAuthContext()
  const styles = useStyles()
  const { logAnalyticsEvent } = useFirebaseContext()
  const { resetPatientRegistration } = usePatientRegistrationStore()

  const settingUpAnalyticsEvent = useCallback(
    (eventDefinitions: AnalyticEvent) => {
      logAnalyticsEvent('at_patient_login', {
        interaction: eventDefinitions?.interaction,
        options: {
          sendTo: SEND_TO,
        },
      })
    },
    [logAnalyticsEvent],
  )

  const redirectIfAuthenticated = (pathname?: string) => {
    if (location?.state?.from) {
      navigate(location?.state?.from?.pathname)
      return
    }

    navigate(pathname)
  }

  useEffect(() => {
    if (getCache('user')) {
      redirectIfAuthenticated('/')
      return
    }

    if (params.clinic) {
      getThemeByUniqueCode(params.clinic, theme)
        .then(res => setThemeColors(res))
        .catch(() => setThemeColors(null))

      setCache('unique-code', params.clinic)
    } else {
      setThemeColors(null)
      setCache('unique-code', '')
    }
    settingUpAnalyticsEvent({ interaction: 'seen' })
    resetPatientRegistration()

    // window?.Appcues?.anonymous()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    async (data: LoginFormData) => {
      try {
        setLoading(true)
        const token = await recaptchaRef?.current?.executeAsync()
        const users: IUser[] = await services.login(
          data?.login,
          data?.password,
          token,
          true,
        )
        const authType = isCPFOrEmail(data?.login)
        signIn(users, authType)
        setLoading(false)
        settingUpAnalyticsEvent({ interaction: 'access_account_success' })
        recaptchaRef?.current?.reset()
        initializeBrowserNotification()

        if (params.clinic) {
          navigate(`/escolha/${params.clinic}`, {
            from: location?.state?.from,
          })
        } else {
          navigate(`/escolha`, {
            from: location?.state?.from,
          })
        }
      } catch (e) {
        recaptchaRef?.current?.reset()
        if (e?.response?.data?.status === 401) {
          form.setError('login', {
            type: 'required',
            message: 'Verifique seu e-mail ou CPF',
          })
          form.setError('password', {
            type: 'required',
            message: 'Verifique sua senha',
          })
        }
        setLoading(false)
        settingUpAnalyticsEvent({ interaction: 'access_account_error' })

        handleLoginError(e, redirectToActivation)
      }
    },
    // eslint-disable-next-line
    [params.clinic, navigate, signIn, logAnalyticsEvent],
  )

  const goToForgotPassword = useCallback(() => {
    logAnalyticsEvent('Pac_Login_EsqueciSenha')

    if (params.clinic) {
      navigate(`/esqueci-senha/${params.clinic}`)
      return
    }

    navigate('/esqueci-senha')
  }, [params.clinic, navigate, logAnalyticsEvent])

  const goToCreateAccount = () => {
    settingUpAnalyticsEvent({ interaction: 'create_account_click' })
    navigate('/cadastro/cpf')
  }

  const redirectToActivation = useCallback(() => {
    let activationUrl = process.env.VITE_APP_ACTIVATION_PAGE_URL + '/passo1'
    if (params?.clinic) {
      activationUrl += `/${params?.clinic}`
    }
    window.open(activationUrl, '_self')
  }, [params?.clinic])

  return (
    <MainLayout
      hideMobileHeader
      showSupportButton
      tagEventName="at_patient_login"
    >
      <div css={styles.container}>
        <div css={styles.loginForm}>
          <RenderIf condition={!!params.clinic && !imageError}>
            <img
              src={`${process.env.VITE_APP_LINK_LOGO_AWS}${params.clinic}.png`}
              alt="logo"
              css={styles.logoWhiteLabel}
              onError={event => {
                event.currentTarget.onerror = null
                setImageError(true)
                event.currentTarget.style.height = 'auto'
              }}
            />
          </RenderIf>
          <RenderIf condition={!params.clinic || imageError}>
            <ConexaLogo base="colorful" inverse width={150} height={32.35} />
          </RenderIf>

          <Text variant="subheading" css={styles.subtitleLoginInstructions}>
            Utilize o seu e-mail ou CPF e senha cadastrada para entrar.
          </Text>

          <form
            ref={formRef}
            autoComplete="off"
            onSubmit={form.handleSubmit(login)}
          >
            <div css={styles.containerFirstInput}>
              <Input
                {...form.register('login')}
                label="E-mail ou CPF"
                placeholder="Digite o seu e-mail ou número do seu CPF"
                fullWidth
                error={form.formState.errors.login?.message?.toString()}
              />
            </div>
            <InputPassword
              label="Senha"
              placeholder="Digite a sua senha"
              fullWidth
              {...form.register('password')}
              error={form.formState.errors.password?.message?.toString()}
            />
            <div css={styles.linkLogin}>
              <Text
                renderAs="span"
                variant="linkUnderline"
                onClick={goToForgotPassword}
              >
                Esqueceu sua senha?
              </Text>
            </div>
            <div css={styles.buttonsWrapper}>
              <Button
                busy={loading}
                fullWidth
                id="at_patient_login_access_account"
              >
                Acessar conta
              </Button>
              <Button
                fullWidth
                id="at_patient_login_create_account"
                variant="secondary"
                onClick={goToCreateAccount}
                type="button"
              >
                Criar conta
              </Button>
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.VITE_APP_RECAPTCHA_SITE_KEY}
            />
          </form>
        </div>
      </div>
    </MainLayout>
  )
}

export default Login
